var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useLayoutEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { createUseStyles } from 'react-jss';
import { Text } from '..';
import { useTheme, ThemeProvider } from '../../theme';
import { usePortalContainer, useRenderTransition, useMemoClasses } from '../../utils/hooks';
import { getTooltipPosition } from './tooltipUtils';
var useStyles = createUseStyles({
    primary: {
        '--RUITooltip-color': 'var(--RUI-color-primary)',
        '--RUITooltip-contrast': 'var(--RUI-color-primary-contrast)',
    },
    secondary: {
        '--RUITooltip-color': 'var(--RUI-color-secondary)',
        '--RUITooltip-contrast': 'var(--RUI-color-secondary-contrast)',
    },
    neutral: {
        '--RUITooltip-color': 'var(--RUI-color-neutral)',
        '--RUITooltip-contrast': 'var(--RUI-color-neutral-contrast)',
    },
    error: {
        '--RUITooltip-color': 'var(--RUI-color-error)',
        '--RUITooltip-contrast': 'var(--RUI-color-error-contrast)',
    },
    warning: {
        '--RUITooltip-color': 'var(--RUI-color-warning)',
        '--RUITooltip-contrast': 'var(--RUI-color-warning-contrast)',
    },
    success: {
        '--RUITooltip-color': 'var(--RUI-color-success)',
        '--RUITooltip-contrast': 'var(--RUI-color-success-contrast)',
    },
    base: {
        position: 'fixed',
        left: function (_a) {
            var pos = _a.pos;
            return "".concat(pos.left, "px");
        },
        top: function (_a) {
            var pos = _a.pos;
            return "".concat(pos.top, "px");
        },
        padding: 'calc(0.4 * var(--RUI-padding)) calc(0.8 * var(--RUI-padding))',
        borderRadius: 'calc(0.5 * var(--RUI-border-radius))',
        backgroundColor: 'var(--RUITooltip-color)',
        color: 'var(--RUITooltip-contrast)',
        opacity: 0,
        transition: function (_a) {
            var transitionDuration = _a.transitionDuration;
            return "opacity ".concat(transitionDuration, "s");
        },
    },
    baseOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Tooltip.base));
    },
    open: {
        opacity: 1,
    },
    openOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Tooltip.open));
    },
    text: {
        whiteSpace: 'nowrap',
        margin: 0,
        fontSize: '0.7em',
        '& ul': {
            paddingLeft: '2ch',
        },
    },
    textOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Tooltip.text));
    },
    arrow: {
        border: '5px solid transparent',
        position: 'absolute',
        width: '0',
        height: '0',
    },
    arrowOverrides: function (_a) {
        var theme = _a.theme;
        return (__assign({}, theme.overrides.Tooltip.arrow));
    },
    arrowRight: {
        borderLeft: '5px solid var(--RUITooltip-color)',
        right: '0',
        top: '50%',
        transform: 'translate(100%, -50%)',
    },
    arrowLeft: {
        borderRight: '5px solid var(--RUITooltip-color)',
        left: '0',
        top: '50%',
        transform: 'translate(-100%, -50%)',
    },
    arrowTop: {
        borderBottom: '5px solid var(--RUITooltip-color)',
        left: '50%',
        top: '0%',
        transform: 'translate(-50%, -100%)',
    },
    arrowBottom: {
        borderTop: '5px solid var(--RUITooltip-color)',
        left: '50%',
        bottom: '0%',
        transform: 'translate(-50%, 100%)',
    },
    arrowHorizontalStart: {
        left: '15px',
    },
    arrowHorizontalEnd: {
        right: '10px',
        left: 'auto',
    },
    arrowVerticalStart: {
        top: '10px',
    },
    arrowVerticalEnd: {
        bottom: '0px',
        top: 'auto',
    },
}, { name: 'RUITooltip' });
var TooltipMessage = function (_a) {
    var className = _a.className, text = _a.text, html = _a.html, arrow = _a.arrow, open = _a.open, justify = _a.justify, position = _a.position, color = _a.color, transitionDuration = _a.transitionDuration, persistent = _a.persistent, anchorRect = _a.anchorRect, props = __rest(_a, ["className", "text", "html", "arrow", "open", "justify", "position", "color", "transitionDuration", "persistent", "anchorRect"]);
    var tooltipRef = useRef(null);
    var theme = useTheme();
    var _b = useState({ top: 0, left: 0 }), pos = _b[0], setPos = _b[1];
    var styles = useStyles({ theme: theme, pos: pos, transitionDuration: transitionDuration });
    var _c = useRenderTransition(open, transitionDuration * 1000), shouldRender = _c.shouldRender, shouldApplyTransition = _c.shouldApplyTransition;
    var render = shouldRender || persistent;
    // update position after tooltip renders and has size
    useLayoutEffect(function () {
        if (render && anchorRect) {
            var tooltipRect = tooltipRef.current.getBoundingClientRect();
            var _a = getTooltipPosition(tooltipRect, anchorRect, position, justify, 10), top_1 = _a.top, left = _a.left;
            setPos({ top: top_1, left: left });
        }
    }, [render, anchorRect, position, justify, tooltipRef]);
    // get all classes
    var tooltipClasses = useMemoClasses([
        styles.base,
        theme.overrides.Tooltip.base && styles.baseOverrides,
        shouldApplyTransition && styles.open,
        shouldApplyTransition && theme.overrides.Tooltip.open && styles.openOverrides,
        color == 'primary' && styles.primary,
        color == 'secondary' && styles.secondary,
        color == 'neutral' && styles.neutral,
        color == 'error' && styles.error,
        color == 'warning' && styles.warning,
        color == 'success' && styles.success,
        'RUITooltip',
        className,
    ], [theme, color, className, shouldApplyTransition]);
    var arrowClasses = useMemoClasses([
        styles.arrow,
        theme.overrides.Tooltip.arrow && styles.arrowOverrides,
        position === 'left' && styles.arrowRight,
        position === 'right' && styles.arrowLeft,
        position === 'top' && styles.arrowBottom,
        position === 'bottom' && styles.arrowTop,
        (position === 'top' || position === 'bottom') && justify === 'start' && styles.arrowHorizontalStart,
        (position === 'top' || position === 'bottom') && justify === 'end' && styles.arrowHorizontalEnd,
        (position === 'left' || position === 'right') && justify === 'start' && styles.arrowVerticalStart,
        (position === 'left' || position === 'right') && justify === 'end' && styles.arrowVerticalEnd,
        'RUITooltip-arrow',
    ], [theme, position, justify]);
    var textClasses = useMemoClasses([styles.text, theme.overrides.Tooltip.text && styles.textOverrides, 'RUITooltip-text'], [theme]);
    // content can be parsed as html
    var content = useMemo(function () {
        return html ? (React.createElement(Text, { className: textClasses, variant: "body2", dangerouslySetInnerHTML: { __html: text } })) : (React.createElement(Text, { className: textClasses, variant: "body2" }, text));
    }, [html, text, textClasses]);
    // needs an extra ThemeProvider because it renders outside on a portal
    var TooltipTree = (React.createElement(ThemeProvider, { theme: theme }, render ? (React.createElement("div", __assign({ className: tooltipClasses, ref: tooltipRef }, props),
        content,
        arrow && React.createElement("span", { className: arrowClasses }))) : null));
    var container = usePortalContainer('Tooltip');
    return createPortal(TooltipTree, container);
};
export default TooltipMessage;
